// @ts-check

import React, { createContext, useContext, useMemo, useState } from "react";

import { useGlobalContext } from "context/GlobalContext";

import { useAppQuery } from "hooks/use-app-query";

const PlatformsContext = createContext({});

export const PlatformsProvider = ({ children }) => {
  const { store, isFetchingStore, isRefetchingStore } = useGlobalContext();

  const [connectablePlatformsLoading, setConnectablePlatformsLoading] = useState([]);
  const [trackingCodeCompletionCount, setTrackingCodeCompletionCount] = useState(null);

  const [invalidAds, setInvalidAds] = useState([]);
  const [invalidAdsPage, setInvalidAdsPage] = useState(1);

  const {
    data: connectablePlatformsResponse,
    refetch: refetchConnectablePlatforms,
    isRefetching: isRefetchingConnectablePlatforms,
    isFetching: isFetchingConnectablePlatforms,
  } = useAppQuery({
    url: "/api/shop/connectable-platforms",
    extraQueryKeys: ["connectablePlatforms"],
    reactQueryOptions: {
      staleTime: 10 * 60 * 1000,
    },
  });

  const loading = isFetchingStore || isRefetchingStore || isFetchingConnectablePlatforms || isRefetchingConnectablePlatforms;

  const connectablePlatforms = connectablePlatformsResponse?.connectablePlatforms || [];

  const facebookConnection = connectablePlatforms.find(platform => platform.key === "facebook");
  const connectedFacebookAccount = Boolean(facebookConnection?.connected_platforms?.length);
  const facebookAccountSetupComplete = Boolean(facebookConnection?.connected_platforms[0]?.has_completed_setup);

  const trackingCodeCompletionProgress = useMemo(() => {
    if (!trackingCodeCompletionCount) {
      return 0;
    }

    if (trackingCodeCompletionCount.totalCount === 0) {
      return 100;
    }

    return (trackingCodeCompletionCount.validCount / trackingCodeCompletionCount.totalCount) * 100;
  }, [trackingCodeCompletionCount]);

  const setupSteps = useMemo(() => {
    const accessScopes = store?.accessScopes?.map(({ handle }) => handle) || [];
    const hasAdditionalAccess =
      accessScopes.includes("read_pixels") &&
      accessScopes.includes("write_pixels") &&
      accessScopes.includes("read_customer_events");

    return [
      { id: 0, complete: hasAdditionalAccess },
      { id: 1, complete: connectedFacebookAccount },
      { id: 2, complete: Boolean(store?.webPixel?.id) },
      { id: 3, complete: facebookAccountSetupComplete || trackingCodeCompletionProgress === 100 },
    ];
  }, [store, connectedFacebookAccount, facebookAccountSetupComplete, trackingCodeCompletionProgress]);

  return (
    <PlatformsContext.Provider
      value={{
        loading,
        connectablePlatforms,
        refetchConnectablePlatforms,
        isFetchingConnectablePlatforms,
        isRefetchingConnectablePlatforms,
        connectablePlatformsLoading,
        setConnectablePlatformsLoading,
        trackingCodeCompletionCount,
        setTrackingCodeCompletionCount,
        trackingCodeCompletionProgress,
        setupSteps,
        invalidAds,
        setInvalidAds,
        invalidAdsPage,
        setInvalidAdsPage,
      }}
    >
      {children}
    </PlatformsContext.Provider>
  );
};

export const usePlatforms = () => {
  const context = useContext(PlatformsContext);
  return context;
};
